import React, { FC, ReactElement, useEffect, useRef, useState } from 'react';

import { FormattedMessage } from 'react-intl';

import { IExercise } from '../Exercise/exercise.interface';
import { IKnowledgeAreaLevel } from '../KnowledgeAreaLevel/knowledge-area-level.interface';

import styles from './exercise-list.module.scss';

import { useUserData } from 'context';
import { ROLE_NAME } from 'enums';
import { useModal } from 'hooks';
import { useRequestLifetime } from 'hooks/useRequestLifetime';
import { reportError } from 'lib/reportError';
import { KnowledgeAreaLevelExercisesApi } from 'services';
import { getAuthSessionRevision, isAuthSessionCurrent } from 'services/auth/session';
import { HandleLocalState } from 'types';
import { VoidFunctionNoArgs } from 'types/common.interface';
import { IKnowledgeAreaDetails } from 'types/domain/knowledgeArea';
import { Button, EmptyState, Exercise, PanelTitle, Plus } from 'views/components';
import { ExerciseDelete } from 'views/pages/KnowledgeArea/ExerciseDelete/ExerciseDelete';
import { ExerciseDetails } from 'views/pages/KnowledgeArea/ExerciseDetails/ExerciseDetails';

interface IExerciseListProps {
  activeLevel: IKnowledgeAreaLevel;
  knowledgeAreaId: number;
  handleKnowledgeAreaDetails: HandleLocalState<IKnowledgeAreaDetails | null>;
  updateKnowledgeAreaDetails: VoidFunctionNoArgs;
}

const sortExercisesById = (items: IExercise[]): IExercise[] =>
  [...items].sort((a, b) => a.id - b.id);

const ExerciseListContent: FC<IExerciseListProps> = (props): ReactElement | null => {
  const {
    updateKnowledgeAreaDetails,
    activeLevel,
    knowledgeAreaId,
    handleKnowledgeAreaDetails,
  } = props;
  const { id, name } = activeLevel;
  const { user } = useUserData();
  const [exercises, setExercises] = useState<IExercise[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const [currentExercise, setCurrentExercise] = useState<IExercise | null>(null);

  const { isOpen: isExerciseDetailsModal, toggle: toggleExerciseDetailsModal } =
    useModal();
  const { isOpen: isExerciseDeleteModal, toggle: toggleExerciseDeleteModal } = useModal();

  const requestedLevelIdRef = useRef(id);
  const requestActive = useRequestLifetime();
  const requestVersion = useRef(0);
  const session = useRef(getAuthSessionRevision());

  async function getExercisesByLevel(): Promise<void> {
    if (!requestActive.current || !isAuthSessionCurrent(session.current)) return;
    requestVersion.current += 1;
    const version = requestVersion.current;
    const requestedLevelId = id;
    requestedLevelIdRef.current = requestedLevelId;
    try {
      const exercisesData =
        await new KnowledgeAreaLevelExercisesApi().getAllExercisesByLevel(id);
      if (
        !requestActive.current ||
        !isAuthSessionCurrent(session.current) ||
        requestVersion.current !== version ||
        requestedLevelIdRef.current !== requestedLevelId
      ) {
        return;
      }
      setExercises(sortExercisesById(exercisesData));
      setIsLoaded(true);
    } catch (err) {
      if (
        requestActive.current &&
        isAuthSessionCurrent(session.current) &&
        requestVersion.current === version
      )
        reportError(err, 'ExerciseList.ExerciseList', true);
    }
  }

  useEffect(() => {
    getExercisesByLevel();
  }, [id]);

  const onAddExerciseClick = (): void => {
    if (currentExercise) {
      setCurrentExercise(null);
    }
    toggleExerciseDetailsModal();
  };

  const onExerciseMenuOpen = (exercise: IExercise): void => {
    setCurrentExercise(exercise);
  };

  return isLoaded ? (
    <>
      <div className={styles.exercises}>
        <PanelTitle
          action={
            exercises.length !== 0 &&
            user!.role.name !== ROLE_NAME.MENTEE && (
              <Button
                icon={<Plus />}
                mode="primary"
                color="accent"
                onClick={onAddExerciseClick}
              >
                <FormattedMessage id="knowledge-area.exercise-management.button" />
              </Button>
            )
          }
        >
          <FormattedMessage
            values={{
              name: <>{name}</>,
            }}
            id="knowledge-area.exercise-management.title"
          />
        </PanelTitle>
        {exercises.length ? (
          <ul className={styles.exercises__list}>
            {exercises.map(exercise => (
              <li key={exercise.id}>
                <Exercise
                  exerciseData={exercise}
                  handleMenuOpen={onExerciseMenuOpen}
                  handleEdit={toggleExerciseDetailsModal}
                  handleRemove={toggleExerciseDeleteModal}
                />
              </li>
            ))}
          </ul>
        ) : (
          <EmptyState
            className={styles['exercises__no-content']}
            title={<FormattedMessage id="knowledge-area.no-exercises.title" />}
            description={
              <FormattedMessage id="knowledge-area.no-exercises.description" />
            }
            primaryAction={{
              icon: <Plus />,
              label: <FormattedMessage id="knowledge-area.no-exercises.button" />,
              onClick: onAddExerciseClick,
            }}
          />
        )}
      </div>
      {isExerciseDetailsModal && (
        <ExerciseDetails
          isOpen={isExerciseDetailsModal}
          handleClose={toggleExerciseDetailsModal}
          currentExercise={currentExercise}
          refetchExercises={getExercisesByLevel}
          levelId={id}
          updateKnowledgeAreaDetails={updateKnowledgeAreaDetails}
        />
      )}

      {currentExercise && (
        <ExerciseDelete
          isOpen={isExerciseDeleteModal}
          handleClose={toggleExerciseDeleteModal}
          currentExercise={currentExercise}
          handleExercises={value => {
            requestVersion.current += 1;
            setExercises(value);
          }}
          knowledgeAreaId={knowledgeAreaId}
          levelId={id}
          handleKnowledgeAreaDetails={handleKnowledgeAreaDetails}
        />
      )}
    </>
  ) : null;
};

const ExerciseList: FC<IExerciseListProps> = props => {
  const { user, currentOrganization } = useUserData();
  const { activeLevel, knowledgeAreaId } = props;
  return (
    <ExerciseListContent
      key={`${knowledgeAreaId}:${activeLevel.id}:${user?.id}:${user?.role.name}:${currentOrganization?.id}`}
      {...props}
    />
  );
};
export { ExerciseList };
